<template>
  <div v-if="reviews.length" class="reviews-carousel pt-5">
    <div class="carousel-frame" @mouseenter="onFrameEnter" @mouseleave="onFrameLeave">
      <div class="carousel-header text-center mb-4">
        <h3 class="display-5 text-white text-uppercase mb-2 fw-bold font-good-brush">Lo Que Dicen De Nosotros</h3>
        <p class="text-white-50 mb-0">Opiniones reales de clientes en Google</p>
      </div>

      <div
        ref="marqueeViewport"
        class="reviews-marquee"
        @mouseenter="onMarqueeEnter"
        @mouseleave="onMarqueeLeave"
      >
        <div class="marquee-track" :style="trackStyle">
          <div ref="segmentRef" class="marquee-segment">
            <article
              v-for="review in reviews"
              :key="review.id"
              class="review-card"
              role="button"
              tabindex="0"
              @mouseenter="onReviewMouseEnter($event, review)"
              @mousemove="onReviewMouseMove($event, review)"
              @mouseleave="onReviewMouseLeave"
              @click="openReviewLink(review)"
              @keydown="onReviewKeydown($event, review)"
            >
              <div class="review-stars" aria-label="5 estrellas">
                <span v-for="n in 5" :key="`${review.id}-star-${n}`">★</span>
              </div>
              <p class="review-text">"{{ review.opinion }}"</p>
              <div class="review-author-row">
                <span class="review-avatar">{{ getInitials(review.nombre) }}</span>
                <p class="review-author mb-0">{{ review.nombre }}</p>
              </div>
            </article>
          </div>

          <div class="marquee-segment" aria-hidden="true">
            <article
              v-for="review in reviews"
              :key="`dup-${review.id}`"
              class="review-card"
              role="button"
              tabindex="0"
              @mouseenter="onReviewMouseEnter($event, review)"
              @mousemove="onReviewMouseMove($event, review)"
              @mouseleave="onReviewMouseLeave"
              @click="openReviewLink(review)"
              @keydown="onReviewKeydown($event, review)"
            >
              <div class="review-stars" aria-label="5 estrellas">
                <span v-for="n in 5" :key="`dup-${review.id}-star-${n}`">★</span>
              </div>
              <p class="review-text">"{{ review.opinion }}"</p>
              <div class="review-author-row">
                <span class="review-avatar">{{ getInitials(review.nombre) }}</span>
                <p class="review-author mb-0">{{ review.nombre }}</p>
              </div>
            </article>
          </div>
        </div>

        <div
          v-if="tooltip.visible"
          ref="tooltipRef"
          class="review-tooltip"
          :style="tooltipStyle"
          @mouseleave="hideTooltip"
        >
          <span>Ver opinion en Google</span>
          <a :href="tooltip.link" target="_blank" rel="noopener noreferrer" @click.stop>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3z"></path>
              <path d="M5 5h6v2H7v10h10v-4h2v6H5V5z"></path>
            </svg>
            Ir a la opinion
          </a>
        </div>

        <div class="edge-overlay edge-overlay-left"></div>
        <div class="edge-overlay edge-overlay-right"></div>
      </div>

      <div class="marquee-scroll-wrap" :class="{ visible: isFrameHovered || isScrollbarActive }">
        <input
          class="marquee-scrollbar"
          type="range"
          min="0"
          :max="scrollbarMax"
          :value="Math.round(scrollOffset)"
          step="1"
          @input="onScrollbarInput"
          @mousedown="onScrollbarPointerDown"
          @touchstart="onScrollbarPointerDown"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const reviewsCsvUrl = '/google_reviews.csv';
const AUTO_SPEED_PX_PER_SECOND = 28;

const reviews = ref([]);
const marqueeViewport = ref(null);
const segmentRef = ref(null);
const tooltipRef = ref(null);
const scrollOffset = ref(0);
const cycleWidth = ref(0);
const isFrameHovered = ref(false);
const isScrollbarActive = ref(false);
const tooltip = ref({
  visible: false,
  x: 0,
  y: 0,
  link: ''
});

let rafId = null;
let lastFrameTimestamp = 0;

const parseCSV = (csvText) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i += 1) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      row.push(field);
      field = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }

      row.push(field);
      const hasData = row.some((item) => item.trim() !== '');
      if (hasData) {
        rows.push(row);
      }

      row = [];
      field = '';
    } else {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    const hasData = row.some((item) => item.trim() !== '');
    if (hasData) {
      rows.push(row);
    }
  }

  if (rows.length === 0) return [];

  const [header, ...dataRows] = rows;
  const nameIndex = header.findIndex((column) => column.trim().toLowerCase() === 'nombre');
  const opinionIndex = header.findIndex((column) => column.trim().toLowerCase() === 'opinion');
  const linkIndex = header.findIndex((column) => column.trim().toLowerCase() === 'link');

  if (nameIndex === -1 || opinionIndex === -1) return [];

  return dataRows
    .map((rowData, index) => ({
      id: `${index}-${rowData[nameIndex] || ''}`,
      nombre: (rowData[nameIndex] || '').trim(),
      opinion: (rowData[opinionIndex] || '').trim(),
      link: linkIndex === -1 ? '' : (rowData[linkIndex] || '').trim()
    }))
    .filter((item) => item.nombre && item.opinion);
};

const trackStyle = computed(() => ({
  transform: `translate3d(-${scrollOffset.value}px, 0, 0)`
}));

const tooltipStyle = computed(() => ({
  left: `${tooltip.value.x}px`,
  top: `${tooltip.value.y}px`
}));

const scrollbarMax = computed(() => Math.max(cycleWidth.value - 1, 1));

const getInitials = (fullName) => {
  const cleanName = (fullName || '').trim();
  if (!cleanName) return '?';

  const parts = cleanName.split(/\s+/).filter(Boolean);
  const initials = parts.slice(0, 2).map((part) => part[0]).join('');
  return initials.toUpperCase();
};

const normalizeOffset = (value) => {
  if (cycleWidth.value <= 0) return 0;
  let normalizedValue = value % cycleWidth.value;
  if (normalizedValue < 0) {
    normalizedValue += cycleWidth.value;
  }
  return normalizedValue;
};

const measureCycleWidth = () => {
  cycleWidth.value = segmentRef.value ? segmentRef.value.scrollWidth : 0;
  scrollOffset.value = normalizeOffset(scrollOffset.value);
};

const isPaused = computed(() => isFrameHovered.value || isScrollbarActive.value || cycleWidth.value === 0);

const moveTooltip = (event) => {
  const offset = 16;
  tooltip.value.x = event.clientX + offset;
  tooltip.value.y = event.clientY + offset;
};

const hideTooltip = () => {
  tooltip.value.visible = false;
};

const onReviewMouseEnter = (event, review) => {
  if (!review.link) return;
  tooltip.value.visible = true;
  tooltip.value.link = review.link;
  moveTooltip(event);
};

const onReviewMouseMove = (event, review) => {
  if (!review.link) return;
  if (!tooltip.value.visible) {
    tooltip.value.visible = true;
  }
  tooltip.value.link = review.link;
  moveTooltip(event);
};

const onReviewMouseLeave = (event) => {
  const nextTarget = event.relatedTarget;
  if (nextTarget && tooltipRef.value && tooltipRef.value.contains(nextTarget)) {
    return;
  }
  hideTooltip();
};

const openReviewLink = (review) => {
  if (!review.link) return;
  window.open(review.link, '_blank', 'noopener,noreferrer');
};

const onReviewKeydown = (event, review) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  openReviewLink(review);
};

const onMarqueeEnter = () => {
  // mantenemos este hook para preservar comportamiento del tooltip
};

const onMarqueeLeave = (event) => {
  const nextTarget = event.relatedTarget;
  if (nextTarget && tooltipRef.value && tooltipRef.value.contains(nextTarget)) {
    return;
  }
  hideTooltip();
};

const onFrameEnter = () => {
  isFrameHovered.value = true;
};

const onFrameLeave = () => {
  isFrameHovered.value = false;
  hideTooltip();
};

const onScrollbarInput = (event) => {
  const nextValue = Number(event.target.value);
  scrollOffset.value = normalizeOffset(nextValue);
};

const onScrollbarPointerDown = () => {
  isScrollbarActive.value = true;
};

const onPointerUp = () => {
  isScrollbarActive.value = false;
};

const animationStep = (timestamp) => {
  if (!lastFrameTimestamp) {
    lastFrameTimestamp = timestamp;
  }

  const deltaSeconds = (timestamp - lastFrameTimestamp) / 1000;
  lastFrameTimestamp = timestamp;

  if (!isPaused.value) {
    scrollOffset.value = normalizeOffset(
      scrollOffset.value + AUTO_SPEED_PX_PER_SECOND * deltaSeconds
    );
  }

  rafId = window.requestAnimationFrame(animationStep);
};

const handleResize = () => {
  measureCycleWidth();
};

onMounted(async () => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('mouseup', onPointerUp);
  window.addEventListener('touchend', onPointerUp);

  try {
    const response = await fetch(reviewsCsvUrl);
    const csvText = await response.text();
    reviews.value = parseCSV(csvText);
    await nextTick();
    measureCycleWidth();
  } catch (error) {
    reviews.value = [];
    console.error('Error loading reviews CSV:', error);
  }

  rafId = window.requestAnimationFrame(animationStep);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('mouseup', onPointerUp);
  window.removeEventListener('touchend', onPointerUp);
  if (rafId) {
    window.cancelAnimationFrame(rafId);
  }
});
</script>

<style scoped>
.reviews-carousel {
  width: 100%;
}

.carousel-frame {
  position: relative;
  border-radius: 18px;
  padding: 2rem 1.25rem;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.35);
}

.reviews-marquee {
  position: relative;
  overflow: hidden;
  user-select: none;
}

.marquee-track {
  display: flex;
  width: max-content;
  will-change: transform;
}

.marquee-segment {
  display: flex;
  gap: 1rem;
  padding: 0.15rem 0;
}

.review-card {
  flex: 0 0 clamp(270px, 31vw, 360px);
  min-height: 230px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
}

.review-card:focus-visible {
  outline: 2px solid rgba(247, 183, 51, 0.95);
  outline-offset: 2px;
}

.review-stars {
  color: #f7b733;
  letter-spacing: 2px;
  margin-bottom: 0.75rem;
}

.review-text {
  color: rgba(255, 255, 255, 0.86);
  line-height: 1.55;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-author {
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.review-author-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.review-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: #fff;
  background: linear-gradient(135deg, rgba(247, 183, 51, 0.95) 0%, rgba(245, 158, 11, 0.95) 100%);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  flex: 0 0 34px;
}

.review-tooltip {
  position: fixed;
  z-index: 6;
  pointer-events: auto;
  min-width: 180px;
  background: rgba(10, 10, 10, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #fff;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font-size: 0.8rem;
  display: grid;
  gap: 0.2rem;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.35);
}

.review-tooltip strong {
  font-size: 0.72rem;
  color: rgba(247, 183, 51, 0.95);
  font-weight: 700;
}

.review-tooltip a {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #f7b733;
  font-size: 0.76rem;
  font-weight: 700;
  text-decoration: none;
}

.review-tooltip a svg {
  width: 13px;
  height: 13px;
  fill: currentColor;
  flex: 0 0 13px;
}

.review-tooltip a:hover {
  text-decoration: underline;
}

.marquee-scroll-wrap {
  position: absolute;
  left: 1.25rem;
  right: 1.25rem;
  bottom: -18px;
  z-index: 5;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
}

.marquee-scroll-wrap.visible {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.marquee-scrollbar {
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  height: 18px;
}

.marquee-scrollbar::-webkit-slider-runnable-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
}

.marquee-scrollbar::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 48px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, #f7b733 0%, #f59e0b 100%);
  margin-top: -2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.marquee-scrollbar::-moz-range-track {
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
}

.marquee-scrollbar::-moz-range-thumb {
  width: 48px;
  height: 8px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(90deg, #f7b733 0%, #f59e0b 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.edge-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  width: min(120px, 14vw);
  pointer-events: none;
  z-index: 2;
}

.edge-overlay-left {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%);
}

.edge-overlay-right {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%);
}

@media (max-width: 1199.98px) {
  .review-card {
    flex-basis: clamp(250px, 44vw, 340px);
  }
}

@media (max-width: 767.98px) {
  .carousel-frame {
    padding: 1.5rem 1rem;
  }

  .marquee-scroll-wrap {
    left: 1rem;
    right: 1rem;
    bottom: -16px;
  }

  .review-card {
    flex-basis: min(85vw, 320px);
    min-height: 190px;
  }

  .edge-overlay {
    width: 58px;
  }
}
</style>